import { BackgroundData, IBackgroundStyle } from "./background-data";
import { IElementWithLink, ILinkData, IPageLinkData, LinkData } from "./link-data";
import { MeshData } from "./mesh-data";
import { IFavoriteData, IMeshSectionBaseData, IMeshedSectionContentData, IScreenData, ISectionStyle } from "./section-base-data";

export interface IMobileHeaderSectionData extends IFavoriteData {
    readonly type: "HeaderMobileSection";
    readonly favorite: null,
    readonly sectionName: "mobile header";
    id?: string | null;
    mobileLayoutType?: MobileLayoutType;
    logo: ILogoData;
    headerBackground: IBackgroundStyle;
    background: IBackgroundStyle;
    navigation: IHeaderNavigationData;
    burgerIcon: IBurgerIconStyle;
    navigationPagesInfo: INavigationPagesInfo | null;
}

export interface IMobileHeaderSectionMenuData {
    mobileLayoutType?: MobileLayoutType;
    logo: ILogoData;
    links: INavigationLinkData[];
    navigationPagesInfo: INavigationPagesInfo | null;
}

export interface IDesktopHeaderSectionData extends IMeshSectionBaseData<ISectionStyle>, IScreenData {
    readonly type: "HeaderDesktopSection";
    id?: string;
    sectionName?: string | null;
    viewType?: HeaderViewType;
}

export type HeaderViewType = "static" | "scroll" | "all";

export interface ILogoData extends IElementWithLink {
    preferredLogoId?: string | null;
    logoMobileHeight?: number;
    altText?: string;
}

export interface IHeaderNavigationData extends INavigationData {
    style: INavigationStyle;
}

export interface INavigationData {
    links: INavigationLinkData[];
    menuType: NavigationMenuType;
}

export enum NavigationMenuType {
    Automatic = "Automatic",
    Custom = "Custom",
    GdprPolicy = "GdprPolicy",
    CheckoutPolicy = "CheckoutPolicy",
}

export interface INavigationLinkData {
    id: string;
    text: string;
    value: ILinkData;
    isActive?: boolean;
    sublinks?: INavigationLinkData[]
}

export interface INavigationStyle {
    color?: string | null;
    boxesColor?: string | null;
}

export interface INavigationPagesInfo {
    loginPagePath: IPageLinkData;
    myOrdersPagePath: IPageLinkData;
    mySubscriptionsPagePath: IPageLinkData;
}

export type MobileLayoutType = "logo-first" | "burger-first";

export type NavigationLayoutType = "HeaderMobileSection" | "MenuWidget";

export enum MobileMenuType {
    Automatic = "Automatic",
    Custom = "Custom"
}

export interface IBurgerIconStyle {
    color?: string | null;
}

export abstract class DesktopHeaderSectionData {

    public static get defaultData(): IDesktopHeaderSectionData {
        return {
            type: "HeaderDesktopSection",
            style: this.defaultSectionStyle,
            content: this.defaultContent,
            background: BackgroundData.defaultHeaderData,
            viewType: "static"
        };
    }

    public static get defaultSectionStyle(): ISectionStyle {
        return {
            minHeightInPc: 12
        };
    }

    public static get defaultContent(): IMeshedSectionContentData {
        return {
            mesh: MeshData.defaultData,
            background: BackgroundData.defaultContentData,
            style: {
                shadowColor: "rgba(32, 32, 32, 0.2)",
                borderRadius: {
                    topLeftPx: 0, topRightPx: 0, bottomLeftPx: 0, bottomRightPx: 0
                },
                maxWidthInPx: null,
                upperAndBottomMarginsInPc: 0
            }
        };
    }

}

export abstract class MobileHeaderSectionData {

    public static get defaultData(): IMobileHeaderSectionData {
        return {
            type: "HeaderMobileSection",
            sectionName: "mobile header",
            favorite: null,
            mobileLayoutType: "logo-first",
            logo: {
                preferredLogoId: null,
                altText: "logo",
                logoMobileHeight: 50,
                linkData: LinkData.noneData
            },
            background: {
                color: "#ECECEC"
            },
            headerBackground: {
                color: "#FFF"
            },
            navigation: {
                links: [],
                menuType: NavigationMenuType.Automatic,
                style: {
                    color: "#202020",
                    boxesColor: "#FFF"
                }
            },
            burgerIcon: {
                color: "#000"
            },
            navigationPagesInfo: null
        };
    }

    private static get defaultLinksStyle(): INavigationStyle {
        return {
            color: "#000",
            boxesColor: "#FFF",
        };
    }

    public static updateWithDefaults(data: IMobileHeaderSectionData): void {
        data.background = this.defaultData.background;
        data.headerBackground = this.defaultData.headerBackground;
        data.logo = this.defaultData.logo;
        data.mobileLayoutType = this.defaultData.mobileLayoutType;
        data.navigation.style = this.defaultLinksStyle;
        data.burgerIcon = this.defaultData.burgerIcon;
    }

}